import { useEffect, useState } from 'react';
import { Menu, X, LogOut, UserRound, Tag, ChevronLeft, ChevronRight, ShieldCheck } from 'lucide-react';
import { api } from '../../lib/api';
import { UsuariosApp } from './UsuariosApp';

// O menu ☰ da visao da loja.
//
// ⚠️ **Nasceu de um relato, nao de um plano**: entrando pela loja, o ☰ do app
// simplesmente sumia — *"O menu 3 tracinhos sumiu, quando estou acessando pela
// loja"*. O cliente tem menu e a loja nao tinha; o mesmo aplicativo passava a ter
// duas gramaticas conforme quem entrava, e o dedo ja tinha aprendido a primeira.
//
// Segue as mesmas convencoes do menu do cliente ([DA-534]): nao fecha por toque no
// fundo, e tem DOIS caminhos de saida visiveis (o X no topo e o Fechar no rodape),
// porque no celular nao existe Esc ([DA-458]).
//
// ⚠️ A **versao** mora aqui porque no ERP ela fica no rodape da pagina, e num app
// instalado em tela cheia nao ha rodape nenhum.
//
// ⛔ **E ela NAO responde "meu celular ja pegou a atualizacao?"** — o `/api/version`
// diz o que esta no SERVIDOR, nao qual bundle este aparelho carregou (memoria
// `reference_rodape_versao_e_do_servidor`). Escrevi "Versao no aparelho" na primeira
// versao deste menu, o que e' exatamente a leitura falsa: ela mentiria justo no caso
// em que alguem a consulta — o celular com bundle velho continuaria exibindo o numero
// novo. O rotulo diz "do sistema" e a linha de baixo entrega a receita que funciona.

const COBRE = '#b45309';

export function MenuLoja({ nome, perfil, aoSair }: { nome: string; perfil: string; aoSair: () => void }) {
  const [aberto, setAberto] = useState(false);
  const [vista, setVista] = useState<'menu' | 'usuarios'>('menu');
  const [versao, setVersao] = useState<string | null>(null);

  // Quem administra o papel dos usuarios do AppCel e' o admin do ERP — a mesma regra
  // que a rota cobra no servidor (`exigirPerfil('admin')`). A tela nao e' a trava:
  // esconder aqui so evita oferecer um caminho que terminaria em 403.
  const podeAdministrar = perfil.toLowerCase() === 'admin';

  useEffect(() => {
    if (!aberto || versao) return;
    api.get<{ versao?: string }>('/api/version')
      .then((r) => setVersao(r.versao ?? null))
      .catch(() => { /* sem versao na tela e' melhor que erro na cara de quem abriu o menu */ });
  }, [aberto, versao]);

  return (
    <>
      <button
        type="button"
        onClick={() => { setVista('menu'); setAberto(true); }}
        aria-label="Menu"
        style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}
      >
        <Menu size={22} />
      </button>

      {aberto && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, backgroundColor: '#f7f7f7', color: '#111', display: 'flex', flexDirection: 'column' }}>
          <header style={{ backgroundColor: COBRE, color: '#fff', padding: '12px 8px 12px 12px', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {vista !== 'menu' ? (
              <button type="button" onClick={() => setVista('menu')} aria-label="Voltar" style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                <ChevronLeft size={22} />
              </button>
            ) : (
              <span style={{ width: 6 }} />
            )}
            <div style={{ flex: 1, fontSize: 17, fontWeight: 800, letterSpacing: 0.3 }}>
              {vista === 'usuarios' ? 'Usuarios do AppCel' : 'Menu'}
            </div>
            <button type="button" onClick={() => setAberto(false)} aria-label="Fechar" style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <X size={22} />
            </button>
          </header>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {vista === 'usuarios' ? <UsuariosApp /> : (
            <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px', backgroundColor: '#fff', borderBottom: '1px solid #e7e5e4' }}>
              <UserRound size={20} style={{ color: COBRE, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16 }}>{nome}</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  Você está atendendo como {perfil}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px', backgroundColor: '#fff', borderBottom: '1px solid #e7e5e4' }}>
              <Tag size={20} style={{ color: COBRE, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16 }}>Versão do sistema</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  {versao ?? 'Conferindo...'}
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3, lineHeight: 1.4 }}>
                  É o que está no servidor. Se a tela parecer velha, feche e abra o app de novo.
                </div>
              </div>
            </div>

            {podeAdministrar && (
              <button
                type="button"
                onClick={() => setVista('usuarios')}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '16px', backgroundColor: '#fff',
                  borderBottom: '1px solid #e7e5e4', fontSize: 16, textAlign: 'left', color: '#111',
                }}
              >
                <ShieldCheck size={20} style={{ color: COBRE, flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                  Usuarios do AppCel
                  <span style={{ display: 'block', fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                    Quem e usuario e quem e administrador
                  </span>
                </span>
                <ChevronRight size={18} style={{ color: '#9ca3af' }} />
              </button>
            )}

            <button
              type="button"
              onClick={() => { setAberto(false); aoSair(); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '16px', backgroundColor: '#fff',
                borderBottom: '1px solid #e7e5e4', fontSize: 16, textAlign: 'left', color: '#111',
              }}
            >
              <LogOut size={20} style={{ color: COBRE, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>
                Sair da visão da loja
                {/* O que acontece depois vai ESCRITO: sair daqui nao e' sair do app,
                    e' voltar pro lado do cliente. Sem esta linha o toque vira aposta. */}
                <span style={{ display: 'block', fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                  Volta para o aplicativo do cliente
                </span>
              </span>
            </button>
            </>
            )}
          </div>

          <div style={{ padding: 14, flexShrink: 0, borderTop: '1px solid #e7e5e4', backgroundColor: '#fff' }}>
            <button
              type="button"
              onClick={() => setAberto(false)}
              style={{ width: '100%', height: 48, borderRadius: 10, border: '1px solid #d6d3d1', backgroundColor: '#fff', color: '#44403c', fontSize: 15.5, fontWeight: 700 }}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </>
  );
}
